import { Box, Button, Switch, Text, VStack, useToast } from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TbBuildingStore, TbFileInvoice, TbFlag, TbScale } from 'react-icons/tb';
import type { QueryObserverResult, RefetchOptions } from '@tanstack/react-query';
import type { InvoicingSettings, VendorFlaggingRule } from '../../../../hooks/useGetSettings';
import { useUpdateSettings } from '../../../../hooks/useUpdateSettings';
import type { SupplierSearchResult } from '../../../../hooks/useSearchSuppliers';
import {
    SettingsCard,
    SettingsFlow,
    SettingsFlowSplit,
    SettingsRow,
    SettingsRuleStep,
    SettingsRuleSteps,
    useSettingsDetailCrumbs,
} from '../../../../components/settings/primitives';
import SearchableSingleSelect from '../../../../components/SearchableSingleSelect';
import VendorSearchField from '../vendors/VendorSearchField';
import { invoicingSettingsForApi, extractUpdateSettingsErrorMessage } from '../../../../utils/invoicingSettingsApi';
import {
    VENDOR_FLAGGING_INVOICE_TYPE_OPTIONS,
    vendorFlaggingInvoiceTypeLabel,
    parseVendorFlaggingRuleRowId,
} from '../../../../utils/vendorFlaggingRules';
import {
    AMOUNT_MISMATCH_DIRECTION_OPTIONS,
    DEFAULT_RECEIVED_BASE_THRESHOLD,
    DEFAULT_RECEIVED_PERCENT_THRESHOLD,
    UNMATCHED_LINE_ITEMS_NEX_SUBMIT_OPTIONS,
    amountMismatchDirectionValue,
    amountMismatchDollarThreshold,
    amountMismatchThresholdHelp,
    flagBothDirectionsFromSelection,
} from './shared';
import { AmountMismatchThresholdFields } from './AmountMismatchThresholdFields';

const SECTION_PATH = '/settings/invoicing-flags';
const SELECT_WIDTH = '280px';

interface VendorFlaggingRuleDraft {
    supplierId: string;
    supplierName: string;
    invoiceType: VendorFlaggingRule['invoiceType'];
    unmatchedLineItemsNexSubmitMode: VendorFlaggingRule['unmatchedLineItemsNexSubmitMode'];
    quantityMismatchFlaggingEnabled: boolean;
    priceMismatchFlaggingEnabled: boolean;
    amountMismatchFlaggingEnabled: boolean;
    amountMismatchPercentThreshold: number;
    amountMismatchBaseDollarThreshold: number;
    amountMismatchDollarThreshold: number;
    amountMismatchFlagBothDirections: boolean;
    requireManualReview: boolean;
}

const emptyDraft = (data: InvoicingSettings): VendorFlaggingRuleDraft => ({
    supplierId: '',
    supplierName: '',
    invoiceType: '*',
    unmatchedLineItemsNexSubmitMode: 'allow_manual_and_automatic',
    quantityMismatchFlaggingEnabled: false,
    priceMismatchFlaggingEnabled: false,
    amountMismatchFlaggingEnabled: false,
    amountMismatchPercentThreshold:
        data.receivedAmountMismatchPercentThreshold ?? DEFAULT_RECEIVED_PERCENT_THRESHOLD,
    amountMismatchBaseDollarThreshold:
        data.receivedAmountMismatchBaseDollarThreshold ?? DEFAULT_RECEIVED_BASE_THRESHOLD,
    amountMismatchDollarThreshold: amountMismatchDollarThreshold(
        data.receivedAmountMismatchDollarThreshold,
        data.receivedAmountMismatchBaseDollarThreshold,
    ),
    amountMismatchFlagBothDirections: data.amountMismatchFlagBothDirections ?? true,
    requireManualReview: false,
});

const draftFromRule = (
    rule: VendorFlaggingRule,
    data: InvoicingSettings,
): VendorFlaggingRuleDraft => ({
    supplierId: rule.supplierId,
    supplierName: rule.supplierName ?? rule.supplierId,
    invoiceType: rule.invoiceType,
    unmatchedLineItemsNexSubmitMode: rule.unmatchedLineItemsNexSubmitMode,
    quantityMismatchFlaggingEnabled: rule.quantityMismatchFlaggingEnabled,
    priceMismatchFlaggingEnabled: rule.priceMismatchFlaggingEnabled,
    amountMismatchFlaggingEnabled: rule.amountMismatchFlaggingEnabled,
    amountMismatchPercentThreshold:
        rule.amountMismatchPercentThreshold ??
        data.receivedAmountMismatchPercentThreshold ??
        DEFAULT_RECEIVED_PERCENT_THRESHOLD,
    amountMismatchBaseDollarThreshold:
        rule.amountMismatchBaseDollarThreshold ??
        data.receivedAmountMismatchBaseDollarThreshold ??
        DEFAULT_RECEIVED_BASE_THRESHOLD,
    amountMismatchDollarThreshold: amountMismatchDollarThreshold(
        rule.amountMismatchDollarThreshold ?? rule.amountMismatchBaseDollarThreshold,
        data.receivedAmountMismatchDollarThreshold ?? data.receivedAmountMismatchBaseDollarThreshold,
    ),
    amountMismatchFlagBothDirections:
        rule.amountMismatchFlagBothDirections ?? data.amountMismatchFlagBothDirections ?? true,
    requireManualReview: rule.requireManualReview,
});

const toStoredRule = (draft: VendorFlaggingRuleDraft): VendorFlaggingRule => ({
    supplierId: draft.supplierId,
    supplierName: draft.supplierName,
    invoiceType: draft.invoiceType,
    unmatchedLineItemsNexSubmitMode: draft.unmatchedLineItemsNexSubmitMode,
    quantityMismatchFlaggingEnabled: draft.quantityMismatchFlaggingEnabled,
    priceMismatchFlaggingEnabled: draft.priceMismatchFlaggingEnabled,
    amountMismatchFlaggingEnabled: draft.amountMismatchFlaggingEnabled,
    ...(draft.amountMismatchFlaggingEnabled
        ? {
              amountMismatchPercentThreshold: draft.amountMismatchPercentThreshold,
              amountMismatchBaseDollarThreshold: draft.amountMismatchBaseDollarThreshold,
              amountMismatchDollarThreshold: draft.amountMismatchDollarThreshold,
              amountMismatchFlagBothDirections: draft.amountMismatchFlagBothDirections,
          }
        : {}),
    requireManualReview: draft.requireManualReview,
});

interface VendorFlaggingRuleDetailSectionProps {
    rowId: string;
    data: InvoicingSettings;
    refetch?: (options?: RefetchOptions) => Promise<QueryObserverResult<unknown, Error>>;
}

const VendorFlaggingRuleDetailSection = ({
    rowId,
    data,
    refetch,
}: VendorFlaggingRuleDetailSectionProps) => {
    const navigate = useNavigate();
    const toast = useToast();
    const updateSettings = useUpdateSettings();
    const parsed = useMemo(() => parseVendorFlaggingRuleRowId(rowId), [rowId]);
    const rules = useMemo(() => data.vendorFlaggingRules ?? [], [data.vendorFlaggingRules]);
    const isAddMode = parsed?.isNew === true;
    const existing = useMemo(() => {
        if (!parsed || parsed.isNew) return undefined;
        return rules.find(
            (rule) =>
                rule.supplierId === parsed.supplierId && rule.invoiceType === parsed.invoiceType,
        );
    }, [parsed, rules]);

    const [draft, setDraft] = useState<VendorFlaggingRuleDraft>(() =>
        existing ? draftFromRule(existing, data) : emptyDraft(data),
    );

    useEffect(() => {
        if (!parsed) navigate(SECTION_PATH, { replace: true });
    }, [parsed, navigate]);

    useEffect(() => {
        if (!isAddMode && parsed && !parsed.isNew && !existing) {
            navigate(SECTION_PATH, { replace: true });
        }
    }, [isAddMode, parsed, existing, navigate]);

    useSettingsDetailCrumbs([
        {
            label: isAddMode ? 'Add flagging rule' : draft.supplierName || 'Flagging rule',
        },
    ]);

    const closeFlow = () => navigate(SECTION_PATH);
    const isSaveDisabled = draft.supplierId.length === 0 || updateSettings.isPending;

    const saveRules = (
        next: VendorFlaggingRule[],
        success: { title: string; description: string },
        failureTitle: string,
    ) => {
        updateSettings.mutate(
            {
                invoicingSettings: invoicingSettingsForApi({
                    ...data,
                    vendorFlaggingRules: next,
                }),
            },
            {
                onSuccess: () => {
                    toast({
                        ...success,
                        status: 'success',
                        duration: 3000,
                        isClosable: true,
                    });
                    refetch?.();
                    closeFlow();
                },
                onError: (error) => {
                    toast({
                        title: failureTitle,
                        description:
                            extractUpdateSettingsErrorMessage(error) ||
                            error.message ||
                            'Please try again.',
                        status: 'error',
                        duration: 5000,
                        isClosable: true,
                    });
                },
            },
        );
    };

    const handleSave = () => {
        if (!draft.supplierId) return;
        const remaining = existing
            ? rules.filter(
                  (rule) =>
                      !(
                          rule.supplierId === existing.supplierId &&
                          rule.invoiceType === existing.invoiceType
                      ),
              )
            : rules;
        const clash = remaining.find(
            (rule) =>
                rule.supplierId === draft.supplierId && rule.invoiceType === draft.invoiceType,
        );
        if (clash) {
            toast({
                title: 'Flagging rule already set',
                description: `${draft.supplierName} already has a rule for ${vendorFlaggingInvoiceTypeLabel(
                    draft.invoiceType,
                )}. Open that row to change it.`,
                status: 'error',
                duration: 6000,
                isClosable: true,
            });
            return;
        }
        saveRules(
            [...remaining, toStoredRule(draft)],
            {
                title: existing ? 'Flagging rule updated' : 'Flagging rule added',
                description: `${draft.supplierName} · ${vendorFlaggingInvoiceTypeLabel(draft.invoiceType)}.`,
            },
            existing ? 'Could not update flagging rule' : 'Could not add flagging rule',
        );
    };

    const handleRemove = () => {
        if (!existing) return;
        saveRules(
            rules.filter(
                (rule) =>
                    !(
                        rule.supplierId === existing.supplierId &&
                        rule.invoiceType === existing.invoiceType
                    ),
            ),
            {
                title: 'Flagging rule removed',
                description: `${existing.supplierName ?? existing.supplierId} has been removed.`,
            },
            'Could not remove flagging rule',
        );
    };

    if (!parsed || (!isAddMode && !existing)) return null;

    const left = (
        <VStack align="stretch" spacing={6}>
            {draft.supplierId ? (
                <SettingsCard title="Vendor">
                    <SettingsRow
                        label={draft.supplierName}
                        control={
                            <Button
                                size="xs"
                                variant="ghost"
                                colorScheme="gray"
                                onClick={() =>
                                    setDraft((prev) => ({
                                        ...prev,
                                        supplierId: '',
                                        supplierName: '',
                                    }))
                                }
                            >
                                Change
                            </Button>
                        }
                    />
                </SettingsCard>
            ) : (
                <Box>
                    <Text fontSize="sm" fontWeight="medium" color="gray.800" mb={1}>
                        Vendor
                    </Text>
                    <Text fontSize="sm" color="gray.500" mb={2.5}>
                        Search the suppliers PartBay knows and pick one. This rule only applies to
                        that vendor.
                    </Text>
                    <VendorSearchField
                        onSelect={(supplier: SupplierSearchResult) =>
                            setDraft((prev) => ({
                                ...prev,
                                supplierId: supplier._id,
                                supplierName: supplier.title,
                            }))
                        }
                        autoFocus={isAddMode}
                    />
                </Box>
            )}

            <SettingsCard title="Invoice type">
                <SettingsRow
                    label="Type"
                    description="Received, credited, return slip, or sublet. All types is less specific than an exact type."
                    control={
                        <SearchableSingleSelect
                            options={[...VENDOR_FLAGGING_INVOICE_TYPE_OPTIONS]}
                            value={draft.invoiceType}
                            onChange={(value) =>
                                setDraft((prev) => ({
                                    ...prev,
                                    invoiceType: value as VendorFlaggingRule['invoiceType'],
                                }))
                            }
                            width={SELECT_WIDTH}
                        />
                    }
                />
            </SettingsCard>

            <SettingsCard
                title="Unmatched lines"
                description="Whether unmatched billable lines may be submitted for this vendor. Overrides the General setting."
            >
                <SettingsRow
                    label="Unmatched line items on Nexsyis submit"
                    control={
                        <SearchableSingleSelect
                            options={[...UNMATCHED_LINE_ITEMS_NEX_SUBMIT_OPTIONS]}
                            value={draft.unmatchedLineItemsNexSubmitMode}
                            onChange={(value) =>
                                setDraft((prev) => ({
                                    ...prev,
                                    unmatchedLineItemsNexSubmitMode:
                                        value as VendorFlaggingRule['unmatchedLineItemsNexSubmitMode'],
                                }))
                            }
                            width={SELECT_WIDTH}
                        />
                    }
                />
            </SettingsCard>

            <SettingsCard
                title="Mismatch flagging"
                description="These toggles override the org-wide mismatch flags for this vendor and type."
            >
                <SettingsRow
                    label="Flag quantity mismatches"
                    description="Quantities that do not match the corresponding Nexsyis line."
                    control={
                        <Switch
                            size="lg"
                            isChecked={draft.quantityMismatchFlaggingEnabled}
                            onChange={(e) =>
                                setDraft((prev) => ({
                                    ...prev,
                                    quantityMismatchFlaggingEnabled: e.target.checked,
                                }))
                            }
                            colorScheme="partbay-blue"
                        />
                    }
                />
                <SettingsRow
                    label="Flag price mismatches"
                    description="Unit or list prices that do not match Nexsyis."
                    control={
                        <Switch
                            size="lg"
                            isChecked={draft.priceMismatchFlaggingEnabled}
                            onChange={(e) =>
                                setDraft((prev) => ({
                                    ...prev,
                                    priceMismatchFlaggingEnabled: e.target.checked,
                                }))
                            }
                            colorScheme="partbay-blue"
                        />
                    }
                />
                <SettingsRow
                    label="Flag total mismatches"
                    description="Extended line totals that differ from Nexsyis beyond the thresholds below."
                    control={
                        <Switch
                            size="lg"
                            isChecked={draft.amountMismatchFlaggingEnabled}
                            onChange={(e) =>
                                setDraft((prev) => ({
                                    ...prev,
                                    amountMismatchFlaggingEnabled: e.target.checked,
                                }))
                            }
                            colorScheme="partbay-blue"
                        />
                    }
                />
            </SettingsCard>

            {draft.amountMismatchFlaggingEnabled && (
                <SettingsCard
                    title="Total mismatch thresholds"
                    description="Defaults to this organization's current amount mismatch thresholds. Applies to this vendor and type only."
                >
                    <SettingsRow
                        label="Differences to flag"
                        description="Both directions flags a higher or lower invoice total. Invoice higher only flags when the invoice total is above Nexsyis. On credited invoices, that means only a smaller credit. Return slips are not amount-flagged."
                        control={
                            <SearchableSingleSelect
                                options={[...AMOUNT_MISMATCH_DIRECTION_OPTIONS]}
                                value={amountMismatchDirectionValue(draft.amountMismatchFlagBothDirections)}
                                onChange={(value) =>
                                    setDraft((prev) => ({
                                        ...prev,
                                        amountMismatchFlagBothDirections:
                                            flagBothDirectionsFromSelection(value),
                                    }))
                                }
                                width={SELECT_WIDTH}
                            />
                        }
                    />
                    <SettingsRow
                        label="Threshold"
                        description={amountMismatchThresholdHelp(
                            draft.amountMismatchBaseDollarThreshold,
                            draft.amountMismatchPercentThreshold,
                            draft.amountMismatchDollarThreshold,
                            draft.amountMismatchFlagBothDirections,
                        )}
                        control={
                            <AmountMismatchThresholdFields
                                idPrefix="Amount mismatch"
                                baseDollar={draft.amountMismatchBaseDollarThreshold}
                                percent={draft.amountMismatchPercentThreshold}
                                dollar={draft.amountMismatchDollarThreshold}
                                onBaseDollarChange={(next) =>
                                    setDraft((prev) => ({
                                        ...prev,
                                        amountMismatchBaseDollarThreshold: next,
                                    }))
                                }
                                onPercentChange={(next) =>
                                    setDraft((prev) => ({
                                        ...prev,
                                        amountMismatchPercentThreshold: next,
                                    }))
                                }
                                onDollarChange={(next) =>
                                    setDraft((prev) => ({
                                        ...prev,
                                        amountMismatchDollarThreshold: next,
                                    }))
                                }
                            />
                        }
                    />
                </SettingsCard>
            )}

            <SettingsCard title="Always review">
                <SettingsRow
                    label="Require manual review"
                    description="Flag this vendor and skip auto-submit even when the invoice matches Nexsyis."
                    control={
                        <Switch
                            size="lg"
                            isChecked={draft.requireManualReview}
                            onChange={(e) =>
                                setDraft((prev) => ({
                                    ...prev,
                                    requireManualReview: e.target.checked,
                                }))
                            }
                            colorScheme="partbay-blue"
                        />
                    }
                />
            </SettingsCard>
        </VStack>
    );

    const unmatchedLabel =
        UNMATCHED_LINE_ITEMS_NEX_SUBMIT_OPTIONS.find(
            (option) => option.value === draft.unmatchedLineItemsNexSubmitMode,
        )?.label ?? draft.unmatchedLineItemsNexSubmitMode;

    return (
        <SettingsFlow
            steps={[{ id: 'rule', label: isAddMode ? 'Add flagging rule' : 'Flagging rule' }]}
            activeStepId="rule"
            onCancel={closeFlow}
            onSave={handleSave}
            saveLabel={isAddMode ? 'Add Rule' : 'Save Rule'}
            isSaveDisabled={isSaveDisabled}
            isSaving={updateSettings.isPending}
            actions={
                existing ? (
                    <Button
                        size="sm"
                        variant="outline"
                        colorScheme="red"
                        onClick={handleRemove}
                        isDisabled={updateSettings.isPending}
                    >
                        Remove Rule
                    </Button>
                ) : undefined
            }
        >
            <SettingsFlowSplit
                leftWidth="45%"
                leftTitle={isAddMode ? 'Add flagging rule' : 'Flagging rule'}
                left={left}
                rightTitle="Rule"
                rightDescription="What PartBay will apply, in the order it reads it."
                right={
                    <SettingsRuleSteps>
                        <SettingsRuleStep
                            index={0}
                            lead="If the vendor is"
                            icon={TbBuildingStore as React.ElementType}
                            values={
                                draft.supplierId
                                    ? [{ label: draft.supplierName, colorScheme: 'green' }]
                                    : []
                            }
                            placeholder="Pick a vendor."
                        />
                        <SettingsRuleStep
                            index={1}
                            lead="and the invoice is"
                            icon={TbFileInvoice as React.ElementType}
                            values={[
                                {
                                    label: vendorFlaggingInvoiceTypeLabel(draft.invoiceType),
                                    colorScheme: 'purple',
                                },
                            ]}
                            placeholder="Pick an invoice type."
                        />
                        <SettingsRuleStep
                            index={2}
                            lead="then unmatched lines"
                            icon={TbScale as React.ElementType}
                            values={[{ label: unmatchedLabel }]}
                            placeholder="Choose unmatched-line submit behavior."
                        />
                        <SettingsRuleStep
                            index={3}
                            lead="and we"
                            icon={TbFlag as React.ElementType}
                            values={[
                                {
                                    label: [
                                        draft.quantityMismatchFlaggingEnabled ? 'flag qty' : 'skip qty',
                                        draft.priceMismatchFlaggingEnabled ? 'flag price' : 'skip price',
                                        draft.amountMismatchFlaggingEnabled
                                            ? 'flag totals'
                                            : 'skip totals',
                                        draft.requireManualReview ? 'always review' : null,
                                    ]
                                        .filter(Boolean)
                                        .join(', '),
                                },
                            ]}
                            placeholder="Choose which mismatches to flag."
                        />
                    </SettingsRuleSteps>
                }
            />
        </SettingsFlow>
    );
};

export default VendorFlaggingRuleDetailSection;
